import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpsertStripeProductLocalization,
  useAdminDeleteStripeProductLocalization,
  getAdminGetStripeProductsQueryKey,
} from '@/api/auth/admin/admin';
import type {
  StripeProductLocalizationResponse,
  StripeProductLocalizationResponseLanguage,
  UpsertStripeProductLocalizationRequestLanguage,
} from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { z } from 'zod';

type LanguageCode = 'en' | 'fr' | 'de' | 'es';

const SUPPORTED_LANGUAGES: { value: LanguageCode; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'es', label: 'Spanish' },
];

const localizationSchema = z.object({
  language: z.enum(['en', 'fr', 'de', 'es']),
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().max(1000).optional(),
});

type LocalizationFormData = z.infer<typeof localizationSchema>;

interface StripeProductLocalizationsDialogProps {
  stripeProductId: string;
  productName: string;
  localizations: StripeProductLocalizationResponse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for managing Stripe product localizations
 */
export function StripeProductLocalizationsDialog({
  stripeProductId,
  productName,
  localizations,
  open,
  onOpenChange,
}: StripeProductLocalizationsDialogProps) {
  const queryClient = useQueryClient();
  const [isAddMode, setIsAddMode] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<LanguageCode | null>(null);
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isOpen: boolean;
    language: LanguageCode | null;
  }>({ isOpen: false, language: null });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<LocalizationFormData>({
    resolver: zodResolver(localizationSchema),
    defaultValues: {
      language: 'en',
      name: '',
      description: '',
    },
  });

  const selectedLanguage = watch('language');

  // Get existing languages to filter out in dropdown
  const existingLanguages = new Set(localizations.map((l) => l.language));

  // Filter available languages when adding (exclude already defined)
  const availableLanguages = isAddMode
    ? SUPPORTED_LANGUAGES.filter((l) => !existingLanguages.has(l.value))
    : SUPPORTED_LANGUAGES;

  const { mutate: upsertLocalization, isPending: isUpserting } = useAdminUpsertStripeProductLocalization({
    mutation: {
      onSuccess: () => {
        toast.success(editingLanguage ? 'Localization updated' : 'Localization added');
        queryClient.invalidateQueries({ queryKey: getAdminGetStripeProductsQueryKey() });
        handleCancelEdit();
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpsertStripeProductLocalization', stripeProductId });
        toast.error('Failed to save localization: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const { mutate: deleteLocalization, isPending: isDeleting } = useAdminDeleteStripeProductLocalization({
    mutation: {
      onSuccess: () => {
        toast.success('Localization deleted');
        queryClient.invalidateQueries({ queryKey: getAdminGetStripeProductsQueryKey() });
        setDeleteDialogState({ isOpen: false, language: null });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'DeleteStripeProductLocalization', stripeProductId });
        toast.error('Failed to delete localization: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const handleCancelEdit = useCallback(() => {
    setIsAddMode(false);
    setEditingLanguage(null);
    reset();
  }, [reset]);

  const handleAddClick = useCallback(() => {
    setIsAddMode(true);
    setEditingLanguage(null);
    reset({
      language: availableLanguages[0]?.value || 'en',
      name: '',
      description: '',
    });
  }, [reset, availableLanguages]);

  const handleEditClick = useCallback(
    (localization: StripeProductLocalizationResponse) => {
      setIsAddMode(false);
      setEditingLanguage(localization.language as LanguageCode);
      reset({
        language: localization.language as LanguageCode,
        name: localization.name,
        description: localization.description || '',
      });
    },
    [reset]
  );

  const handleDeleteClick = useCallback((language: StripeProductLocalizationResponseLanguage) => {
    setDeleteDialogState({ isOpen: true, language: language as LanguageCode });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteDialogState.language) {
      deleteLocalization({ stripeProductId, language: deleteDialogState.language });
    }
  }, [deleteLocalization, stripeProductId, deleteDialogState.language]);

  const onSubmit = useCallback(
    (data: LocalizationFormData) => {
      upsertLocalization({
        stripeProductId,
        data: {
          language: data.language as UpsertStripeProductLocalizationRequestLanguage,
          name: data.name,
          description: data.description || undefined,
        },
      });
    },
    [upsertLocalization, stripeProductId]
  );

  const isEditing = isAddMode || editingLanguage !== null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Product Localizations</DialogTitle>
            <DialogDescription>
              Manage translations for product &quot;{productName}&quot;
            </DialogDescription>
          </DialogHeader>

          {isEditing ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="language">Language</Label>
                <Select
                  value={selectedLanguage}
                  onValueChange={(value) => setValue('language', value as LanguageCode)}
                  disabled={!!editingLanguage}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    {(editingLanguage ? SUPPORTED_LANGUAGES : availableLanguages).map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.language && <p className="text-sm text-destructive">{errors.language.message}</p>}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register('name')} placeholder="Localized product name" />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Localized product description (optional)"
                  rows={3}
                />
                {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={isUpserting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isUpserting}>
                  {isUpserting ? 'Saving...' : editingLanguage ? 'Update' : 'Add'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleAddClick}
                  disabled={availableLanguages.length === 0}
                  title={availableLanguages.length === 0 ? 'All languages have localizations' : undefined}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Localization
                </Button>
              </div>

              {localizations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No localizations configured. Add a localization to provide translations for this product.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Language</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {localizations.map((loc) => (
                      <TableRow key={loc.id}>
                        <TableCell>
                          {SUPPORTED_LANGUAGES.find((l) => l.value === loc.language)?.label || loc.language}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">{loc.name}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-muted-foreground">
                          {loc.description || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditClick(loc)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(loc.language)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialogState.isOpen}
        onOpenChange={(isOpen) => setDeleteDialogState((prev) => ({ ...prev, isOpen }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Localization</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the{' '}
              {SUPPORTED_LANGUAGES.find((l) => l.value === deleteDialogState.language)?.label} localization? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
